import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { PublicPageLayout } from '@/components/landing/PublicPageLayout';
import { Container, KeystoneMark } from '@/components/g5';
import { LabelChip, StatPill } from '@/components/aura';
import { Input } from '@/components/ui/input';
import { Search, BookOpen, ArrowRight } from 'lucide-react';
import { CTABanner } from '@/components/landing/CTABanner';
import { SEO_CONFIG } from '@/config/seo';
import {
  glossaryTerms,
  glossaryCategories,
  glossaryLetters,
  type GlossaryTerm,
  type GlossaryCategory,
} from '@/constants/glossaryTerms';

const card = { background: 'var(--g5-card)', border: '1px solid var(--g5-line)' } as const;
const ink = { color: 'var(--g5-ink)' } as const;
const sub = { color: 'var(--g5-sub)' } as const;
const line = 'var(--g5-line)';
const accent = 'var(--g5-accent)';
const tint = 'var(--g5-tint)';

/* ─── Filter pill classes ─── */
const pillBase = 'rounded-full px-3.5 py-1.5 text-xs font-semibold transition-colors duration-150';
const pillSelected = 'bg-[color:var(--g5-accent)] text-[color:var(--g5-on-accent)]';
const pillIdle =
  'bg-[color:var(--g5-tint)] text-[color:var(--g5-accent)] hover:bg-[color:color-mix(in_srgb,var(--g5-accent)_16%,var(--g5-tint))]';

/* ─── Term Card ─── */
function TermCard({ term }: { term: GlossaryTerm }) {
  // Show first sentence as preview
  const preview = term.definition.split('. ').slice(0, 1).join('. ') + '.';

  return (
    <Link to={`/glossary/${term.slug}`} className="group block h-full">
      <div className="gl-card flex h-full flex-col rounded-2xl p-5" style={card}>
        <div className="flex items-start gap-2.5">
          <KeystoneMark size={13} className="mt-1.5" />
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
              <h3 className="font-display text-[0.95rem] font-bold leading-snug tracking-tight" style={ink}>
                {term.term}
              </h3>
              {term.abbreviation && (
                <span
                  className="shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide"
                  style={{ background: tint, color: accent }}
                >
                  {term.abbreviation}
                </span>
              )}
            </div>
            <p className="mt-1.5 line-clamp-2 text-sm leading-relaxed" style={sub}>
              {preview}
            </p>
          </div>
        </div>
        <span
          className="mt-3 inline-flex items-center gap-1 text-xs font-semibold opacity-0 transition-opacity duration-150 group-hover:opacity-100"
          style={{ color: accent }}
        >
          Read more <ArrowRight className="h-3 w-3" />
        </span>
      </div>
    </Link>
  );
}

/* ─── JSON-LD ─── */
const jsonLd = {
  '@context': 'https://schema.org',
  '@type': 'CollectionPage',
  name: 'Appraisal Glossary — Ashlar App',
  description:
    'Comprehensive glossary of real estate appraisal terminology. Over 100 terms covering valuation approaches, USPAP, UAD, property characteristics, and NJ-specific regulations.',
  url: `${SEO_CONFIG.baseUrl}/glossary`,
  mainEntity: {
    '@type': 'DefinedTermSet',
    name: 'Real Estate Appraisal Glossary',
    url: `${SEO_CONFIG.baseUrl}/glossary`,
    hasDefinedTerm: glossaryTerms.map((t) => ({
      '@type': 'DefinedTerm',
      name: t.term,
      url: `${SEO_CONFIG.baseUrl}/glossary/${t.slug}`,
    })),
  },
};

export default function Glossary() {
  const [search, setSearch] = useState('');
  const [activeCategory, setActiveCategory] = useState<GlossaryCategory | null>(null);

  const isSearching = search.trim().length > 0;
  const query = search.toLowerCase();

  const filteredTerms = useMemo(() => {
    let terms = glossaryTerms;

    if (activeCategory) {
      terms = terms.filter((t) => t.category === activeCategory);
    }

    if (isSearching) {
      terms = terms.filter(
        (t) =>
          t.term.toLowerCase().includes(query) ||
          t.definition.toLowerCase().includes(query) ||
          (t.abbreviation && t.abbreviation.toLowerCase().includes(query)),
      );
    }

    return terms;
  }, [activeCategory, isSearching, query]);

  // Always group the visible set by first letter — one section per letter, one
  // unique anchor per letter. This is what the alphabet jump-nav targets.
  const lettered = useMemo(() => {
    const sorted = [...filteredTerms].sort((a, b) => a.term.localeCompare(b.term));
    const byLetter = new Map<string, GlossaryTerm[]>();
    for (const t of sorted) {
      const letter = t.term[0].toUpperCase();
      const arr = byLetter.get(letter) || [];
      arr.push(t);
      byLetter.set(letter, arr);
    }
    return [...byLetter.entries()];
  }, [filteredTerms]);

  // Letters present in current filtered set (drives which jump buttons are live)
  const activeLetters = useMemo(
    () => new Set(filteredTerms.map((t) => t.term[0].toUpperCase())),
    [filteredTerms],
  );

  const scrollToLetter = (letter: string) => {
    const el = document.getElementById(`letter-${letter}`);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <PublicPageLayout
      title="Appraisal Glossary"
      description="Comprehensive glossary of real estate appraisal terminology. Over 100 terms covering valuation approaches, USPAP, UAD, property characteristics, and NJ-specific regulations."
      breadcrumbLabel="Glossary"
      canonicalPath="/glossary"
      jsonLd={jsonLd}
    >
      {/* Hero */}
      <section className="relative overflow-hidden" style={{ borderBottom: `1px solid ${line}` }}>
        <Container className="relative py-16 md:py-24">
          <LabelChip>Glossary</LabelChip>
          <h1 className="mt-5 max-w-3xl font-display text-4xl font-extrabold leading-[1.05] tracking-tight md:text-5xl" style={ink}>
            Appraisal terminology.
          </h1>
          <p className="mt-5 max-w-2xl text-lg leading-relaxed" style={sub}>
            {glossaryTerms.length}+ terms covering valuation approaches, USPAP standards, UAD compliance, property characteristics, and NJ-specific regulations — written in plain language and linked together.
          </p>
          <div className="mt-8 flex flex-wrap gap-2.5">
            <StatPill value={`${glossaryTerms.length}`} label="terms defined" />
            <StatPill value={`${glossaryCategories.length}`} label="categories" />
            <StatPill value={`${glossaryLetters.length}`} label="letters A–Z" />
          </div>
        </Container>
      </section>

      {/* Search + Filters (sticky) */}
      <div
        className="sticky top-16 z-20 backdrop-blur-sm"
        style={{
          background: 'color-mix(in srgb, var(--g5-bg) 92%, transparent)',
          borderBottom: `1px solid ${line}`,
        }}
      >
        <Container className="py-4">
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
              {/* Search */}
              <div className="relative w-full max-w-md">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2" style={sub} />
                <Input
                  placeholder="Search terms..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="rounded-full pl-9"
                />
              </div>
              <p className="text-xs font-medium" style={sub}>
                Showing {filteredTerms.length} of {glossaryTerms.length} terms
              </p>
            </div>

            {/* Category pills */}
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setActiveCategory(null)}
                className={`${pillBase} ${!activeCategory ? pillSelected : pillIdle}`}
              >
                All ({glossaryTerms.length})
              </button>
              {glossaryCategories.map((cat) => {
                const count = glossaryTerms.filter((t) => t.category === cat).length;
                return (
                  <button
                    key={cat}
                    onClick={() => setActiveCategory(activeCategory === cat ? null : cat)}
                    className={`${pillBase} ${activeCategory === cat ? pillSelected : pillIdle}`}
                  >
                    {cat} ({count})
                  </button>
                );
              })}
            </div>

            {/* Alphabet jump-nav */}
            <div className="flex flex-wrap gap-1">
              {glossaryLetters.map((letter) => {
                const isActive = activeLetters.has(letter);
                return (
                  <button
                    key={letter}
                    disabled={!isActive}
                    onClick={() => scrollToLetter(letter)}
                    className={`h-8 w-8 rounded-full text-xs font-semibold transition-colors duration-150 ${
                      isActive
                        ? pillIdle
                        : 'cursor-default text-[color:color-mix(in_srgb,var(--g5-sub)_35%,transparent)]'
                    }`}
                  >
                    {letter}
                  </button>
                );
              })}
            </div>
          </div>
        </Container>
      </div>

      {/* Term listing — letter-grouped, dense, always rendered (no scroll-gated fade) */}
      <Container className="py-12 md:py-16">
        {filteredTerms.length === 0 ? (
          <div className="mx-auto max-w-md rounded-3xl py-16 text-center" style={card}>
            <BookOpen
              className="mx-auto mb-4 h-12 w-12"
              style={{ color: 'color-mix(in srgb, var(--g5-sub) 40%, transparent)' }}
            />
            <h2 className="mb-2 font-display text-lg font-bold tracking-tight" style={ink}>No terms found</h2>
            <p className="px-6 leading-relaxed" style={sub}>
              {isSearching
                ? `Nothing matches "${search.trim()}". Try a different word, or clear the filters.`
                : 'Try clearing the category filter to see every term.'}
            </p>
          </div>
        ) : (
          <div className="space-y-12">
            {lettered.map(([letter, terms]) => (
              <section key={letter} id={`letter-${letter}`} className="scroll-mt-[12rem]">
                <div className="mb-5 flex items-center gap-4">
                  <span
                    className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl font-display text-lg font-extrabold"
                    style={{ background: tint, color: accent }}
                  >
                    {letter}
                  </span>
                  <div className="h-px flex-1" style={{ background: line }} />
                  <span className="text-xs font-medium tabular-nums" style={sub}>
                    {terms.length} term{terms.length !== 1 ? 's' : ''}
                  </span>
                </div>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  {terms.map((t) => (
                    <TermCard key={t.slug} term={t} />
                  ))}
                </div>
              </section>
            ))}
          </div>
        )}
      </Container>

      {/* CTA */}
      <CTABanner
        heading="Ready to put these terms to work?"
        subtext="Join the modern appraisal platform — flexible pricing that scales with your practice."
        secondaryLabel="View Pricing"
        secondaryHref="/pricing"
      />

      <style>{`.gl-card { transition: transform .18s ease, border-color .18s ease; } .gl-card:hover { transform: translateY(-3px); border-color: color-mix(in srgb, var(--g5-key) 55%, var(--g5-line)); }`}</style>
    </PublicPageLayout>
  );
}
